import React from 'react';
import { Avatar, Box, Button, Chip, Typography } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import { ariaAvatarFallbackSx, ariaCardSurfaceSx } from '../ariaHandsoffSurfaces';
import { ARIA_CARD_ACCENT } from '../ariaMessageTypes';

const CandidateCard = ({ candidate }) => {
  const status = candidate.introductionStatus || 'pending';

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 340 },
        maxWidth: '100%',
        minWidth: { sm: 300 },
        borderRadius: '16px',
        ...ariaCardSurfaceSx,
        boxShadow: 2,
        px: 1.5,
        py: 1.35,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Avatar
          src={candidate.avatarUrl || undefined}
          alt={candidate.fullName}
          sx={{ width: 44, height: 44, ...ariaAvatarFallbackSx }}
        >
          {candidate.fullName?.charAt(0)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.primary', lineHeight: 1.3 }}
          >
            {candidate.fullName}
          </Typography>
          {candidate.profession ? (
            <Typography
              noWrap
              sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35, mt: 0.15 }}
            >
              {candidate.profession}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {candidate.sharedInterestLabels?.length ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
          {candidate.sharedInterestLabels.map((label) => (
            <Chip
              key={label}
              label={label}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: ARIA_CARD_ACCENT,
                bgcolor: 'background.paper',
                border: `1px solid ${ARIA_CARD_ACCENT}`,
                borderRadius: '8px',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          ))}
        </Box>
      ) : null}

      {candidate.reason ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            bgcolor: (theme) => theme.portal?.accentSoft ?? theme.palette.action.hover,
            border: '1px solid',
            borderColor: (theme) => theme.portal?.accentMuted ?? theme.palette.divider,
            borderRadius: '12px',
            px: 1.25,
            py: 1,
            mb: 1.25,
          }}
        >
          <PeopleAltOutlinedIcon sx={{ fontSize: 16, color: 'primary.main', mt: '1px' }} />
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.45 }}>
            {candidate.reason}
          </Typography>
        </Box>
      ) : null}

      {status === 'declined' ? (
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
          Noted
          {candidate.declineReason ? ` — "${candidate.declineReason}"` : ''}
        </Typography>
      ) : null}

      {status === 'accepted' ? (
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#166534' }}>
          Introduction requested
        </Typography>
      ) : null}

      {status === 'pending' ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            disabled
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8125rem',
              borderRadius: '999px',
              py: 0.85,
              bgcolor: ARIA_CARD_ACCENT,
              boxShadow: 'none',
              '&.Mui-disabled': {
                bgcolor: ARIA_CARD_ACCENT,
                color: '#ffffff',
                opacity: 0.55,
              },
            }}
          >
            Introduce us
          </Button>
          <Button
            variant="outlined"
            disabled
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8125rem',
              borderRadius: '999px',
              px: 1.75,
              py: 0.85,
              flexShrink: 0,
              color: 'text.secondary',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              whiteSpace: 'nowrap',
              '&.Mui-disabled': {
                color: 'text.disabled',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              },
            }}
          >
            Not a fit
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * People-to-meet cards (message.data.ariaMessageType = people_to_meet).
 * Display-only in admin — status drives UI; buttons never fire actions.
 * Empty candidates → renders nothing.
 */
const AriaPeopleToMeetCards = ({ people }) => {
  const candidates = people?.candidates ?? [];
  if (!candidates.length) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        width: { xs: '100%', sm: 340 },
        maxWidth: '100%',
        minWidth: { sm: 300 },
      }}
    >
      {candidates.map((candidate) => (
        <CandidateCard key={candidate.key} candidate={candidate} />
      ))}
    </Box>
  );
};

export default AriaPeopleToMeetCards;
